import ShuffleIcon from '@mui/icons-material/Shuffle'
import { useContext, useEffect, useState } from 'react'
import { BusinessProfileContext } from 'src/business/context/BusinessProfileContext'
import { GenericDisplayFlex, GenericDisplayFlexColumn } from 'src/business/layout/styledComponents'
import { DashBoardItemHeading } from 'src/business/views/dashboard/dashboardStyles'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'
import {
  BarGraphHeader,
  BarGraphLineContainer,
  BarGraphLineContainerWorld,
  BarGraphLineImage,
  BarGraphMaxScoreLine,
  GlobalScoreContainer,
  GraphLinesContainer,
  LegendsContainer,
  MentalStateBarGraphStatContainer,
  MentalStateBarGraphStatName,
  MentalStateBarGraphTitle,
  MentalStateCollapsedContainer,
  MentalStateCollapsedItemContainer,
  MentalStateExpandedContainer,
  MentalStateExpandedGraphContainer,
  MentalStateExpandedIndexContainer,
  MentalStateExpandedIndexItemAverageScore,
  MentalStateExpandedIndexItemContainer,
  MentalStateExpandedIndexItemImage,
  MentalStateExpandedIndexItemMaxScore,
  StyledDeltaImage,
  TimeDurationButton,
} from './MentalStateIndexStyles'
import { fallbackBPMentalStateGraphServerData } from './utils'
import whiteGlobeImage from 'src/assets/white_globe.png'

export const MentalStateIndexComponent = () => {
  const { businessCustomerDetails } = useContext(BusinessProfileContext)

  const [selectedIndex, setSelectedIndex] = useState(0)
  const [selectedTimeDurationIndex, setSelectedTimeDurationIndex] = useState(0)
  const [selectedView, setSelectedView] = useState('collapsed')
  const [serverData, setServerData] = useState()

  useEffect(() => {
    // Backend endpoint GET /api/secure/bc/mental-state-index does not exist yet.
    // Using fallback data until implemented to avoid NoHandlerFoundException server errors.
    // When backend adds the endpoint, replace with:
    //   getBPDataWithoutParams('/api/secure/bc/mental-state-index').then(res => {
    //     if (res?.status === 200 && res?.data) setServerData(res.data)
    //     else setServerData(fallbackBPMentalStateGraphServerData)
    //   }).catch(() => setServerData(fallbackBPMentalStateGraphServerData))
    setServerData(fallbackBPMentalStateGraphServerData)
  }, [])

  return (
    <>
      <GenericDisplayFlex sb style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          <DashBoardItemHeading>Company Indexes</DashBoardItemHeading>
          <span
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && (selectedView === 'collapsed' ? setSelectedView('expanded') : setSelectedView('collapsed'))}
            style={{
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              color: '#5F2EC7',
              textDecoration: 'none',
              padding: '12px 16px',
              minHeight: '44px',
              minWidth: '44px',
              display: 'inline-flex',
              alignItems: 'center',
              borderRadius: '8px',
              transition: 'background 0.2s, color 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(95, 46, 199, 0.1)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent'
            }}
            onClick={() => {
              if (selectedView === 'collapsed') {
                setSelectedView('expanded')
              } else {
                setSelectedView('collapsed')
              }
            }}
          >
            {selectedView === 'collapsed' ? 'See detailed view →' : '← See collapsed view'}
          </span>
        </div>
        {selectedView === 'collapsed' && (
          <TimeDurationButton
            onClick={() =>
              setSelectedTimeDurationIndex((prev) => {
                if (serverData?.[selectedIndex]?.timeDurations?.length - 1 === prev) {
                  return 0
                }
                return prev + 1
              })
            }
          >
            <div>
              {serverData?.[selectedIndex]?.timeDurations?.[selectedTimeDurationIndex]?.name}
            </div>
            <ShuffleIcon sx={{ fontSize: '8', color: 'white' }} />
          </TimeDurationButton>
        )}
      </GenericDisplayFlex>
      {selectedView === 'collapsed' && (
        <MentalStateCollapsedContainer>
          {serverData?.map((item, index) => {
            return (
              <MentalStateCollapsedItemContainer
                selected={selectedIndex === index}
                key={index}
                mentalStateColor={item?.color}
                onClick={() => {
                  setSelectedIndex(index)
                  setSelectedView('expanded')
                }}
              >
                <div>{item?.name}</div>
                <MentalStateExpandedIndexItemImage src={getImageLink(item?.image)} />
                <div>
                  <MentalStateExpandedIndexItemAverageScore
                    goodScore={
                      item?.timeDurations?.[selectedTimeDurationIndex]?.averageScore /
                        item?.timeDurations?.[selectedTimeDurationIndex]?.maxScore >=
                      0.6
                    }
                  >
                    {item?.timeDurations?.[selectedTimeDurationIndex]?.averageScore}
                  </MentalStateExpandedIndexItemAverageScore>
                  <MentalStateExpandedIndexItemMaxScore>
                    / {item?.timeDurations?.[selectedTimeDurationIndex]?.maxScore}
                  </MentalStateExpandedIndexItemMaxScore>
                </div>
                <div className="d-flex justify-content-center gap-2 align-items-center">
                  {getDeltaImageBasedOnDelta(
                    item?.timeDurations?.[selectedTimeDurationIndex]?.delta || 0,
                  )}
                  <span style={{ fontSize: '18px', fontWeight: '300' }}>
                    {item?.timeDurations?.[selectedTimeDurationIndex]?.delta || 0}
                  </span>
                </div>
                <div>
                  Global Score:{' '}
                  {item?.timeDurations?.[selectedTimeDurationIndex]?.averageGlobalScore} /{' '}
                  {item?.timeDurations?.[selectedTimeDurationIndex]?.maxScore}
                </div>
              </MentalStateCollapsedItemContainer>
            )
          })}
        </MentalStateCollapsedContainer>
      )}
      {selectedView === 'expanded' && (
        <MentalStateExpandedContainer>
          <MentalStateExpandedIndexContainer>
            {serverData?.map((item, index) => {
              return (
                <MentalStateExpandedIndexItemContainer
                  selected={selectedIndex === index}
                  key={index}
                  mentalStateColor={item?.color}
                  onClick={() => {
                    setSelectedIndex(index)
                  }}
                >
                  <div>{item?.name}</div>
                  {/* <MentalStateExpandedIndexItemImage src={`${mediaURL}${item?.image}`} /> */}
                  <MentalStateExpandedIndexItemImage src={getImageLink(item?.image)} />
                  <div>
                    <MentalStateExpandedIndexItemAverageScore
                      goodScore={
                        item?.timeDurations?.[selectedTimeDurationIndex]?.averageScore /
                          item?.timeDurations?.[selectedTimeDurationIndex]?.maxScore >=
                        0.6
                      }
                      selected={selectedIndex === index}
                    >
                      {item?.timeDurations?.[selectedTimeDurationIndex]?.averageScore}
                    </MentalStateExpandedIndexItemAverageScore>
                    <MentalStateExpandedIndexItemMaxScore>
                      {' '}
                      / {item?.timeDurations?.[selectedTimeDurationIndex]?.maxScore}
                    </MentalStateExpandedIndexItemMaxScore>
                  </div>
                </MentalStateExpandedIndexItemContainer>
              )
            })}
          </MentalStateExpandedIndexContainer>

          <MentalStateExpandedGraphContainer>
            <BarGraphHeader>
              <GenericDisplayFlexColumn>
                <MentalStateBarGraphTitle>
                  {serverData?.[selectedIndex]?.name}
                </MentalStateBarGraphTitle>
                {/* <div>January 01, 2023 - December 31, 2023</div> */}
              </GenericDisplayFlexColumn>
              <GlobalScoreContainer>
                Global Score -{' '}
                {
                  serverData?.[selectedIndex]?.timeDurations?.[selectedTimeDurationIndex]
                    ?.averageGlobalScore
                }{' '}
                /{' '}
                {serverData?.[selectedIndex]?.timeDurations?.[selectedTimeDurationIndex]?.maxScore}
              </GlobalScoreContainer>
              <TimeDurationButton
                onClick={() =>
                  setSelectedTimeDurationIndex((prev) => {
                    if (serverData?.[selectedIndex]?.timeDurations?.length - 1 === prev) {
                      return 0
                    }
                    return prev + 1
                  })
                }
              >
                <div>
                  {serverData?.[selectedIndex]?.timeDurations?.[selectedTimeDurationIndex]?.name}
                </div>
                <ShuffleIcon sx={{ fontSize: '8', color: 'white' }} />
              </TimeDurationButton>
            </BarGraphHeader>

            {serverData?.[selectedIndex]?.timeDurations?.[selectedTimeDurationIndex]?.data?.map(
              (selectedTimeDurationStatOption, selectedTimeDurationStatOptionIndex) => {
                return (
                  <MentalStateBarGraphStatContainer key={selectedTimeDurationStatOptionIndex}>
                    <MentalStateBarGraphStatName>
                      {selectedTimeDurationStatOption?.name}
                    </MentalStateBarGraphStatName>
                    <GraphLinesContainer>
                      <BarGraphLineContainer value={selectedTimeDurationStatOption?.percent}>
                        {/* <BarGraphLineImage top src={`${mediaURL}${selectedTimeDurationStatOption?.image}`} /> */}
                        <BarGraphLineImage
                          top
                          src={getImageLink(selectedTimeDurationStatOption?.image)}
                        />
                      </BarGraphLineContainer>
                      <BarGraphMaxScoreLine />
                      <BarGraphLineContainerWorld
                        value={selectedTimeDurationStatOption?.globalPercent}
                      >
                        <BarGraphLineImage
                          globe={true}
                          src={whiteGlobeImage}
                        />
                      </BarGraphLineContainerWorld>
                    </GraphLinesContainer>
                  </MentalStateBarGraphStatContainer>
                )
              },
            )}

            <LegendsContainer>
              <GenericDisplayFlexColumn aic>
                <BarGraphLineContainer style={{ width: '60px' }} />
                <MentalStateBarGraphStatName
                  style={{ width: 'auto' }}
                >{`${businessCustomerDetails?.companyName} scores`}</MentalStateBarGraphStatName>
              </GenericDisplayFlexColumn>
              <GenericDisplayFlexColumn aic>
                <BarGraphLineContainerWorld style={{ width: '60px' }} />
                <MentalStateBarGraphStatName style={{ width: 'auto' }}>
                  World wide scores
                </MentalStateBarGraphStatName>
              </GenericDisplayFlexColumn>
            </LegendsContainer>
          </MentalStateExpandedGraphContainer>
        </MentalStateExpandedContainer>
      )}
    </>
  )
}

function getDeltaImageBasedOnDelta(delta) {
  return (
    <>
      <StyledDeltaImage
        style={{ display: delta === 0 ? 'unset' : 'none' }}
        src={getImageLink('MEDIA-5ff5fa276d69417d83fdafce855fb6f3')}
      />
      <StyledDeltaImage
        style={{ display: delta > 0 ? 'unset' : 'none' }}
        src={getImageLink('MEDIA-d56a3b64480444289952ae3fd3b7fe3b')}
      />
      <StyledDeltaImage
        style={{ display: delta < 0 ? 'unset' : 'none' }}
        src={getImageLink('MEDIA-0cda072ed875400abace0cd6e5735ab1')}
      />
    </>
  )
}
